import AnimatedContainer from "../../container/animatedContainer";
import type { ReactNode } from "react";
import { BsTicketPerforated } from "react-icons/bs";
import { CgChevronLeftO, CgChevronRightO } from "react-icons/cg";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { GoArrowRight } from "react-icons/go";
import { HiOutlineExclamationCircle } from "react-icons/hi2";
import { FiCalendar, FiMapPin, FiMic, FiMusic } from "react-icons/fi";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useParams, useSearchParams } from "react-router-dom";
import { useGetEventsQuery, useGetSingleEventQuery } from "../../api/event";
import { Ticket } from "../../types/event";
import { usePaymentMutation } from "../../api/tickets";
import { useForm } from "react-hook-form";
import mtn_logo from "../../assets/images/mtn-logo.png";
import { useAppSelector } from "../../store";
import { PaymentPayload } from "../../types/tickets";
import { useVerifyPromoCodeMutation } from "../../api/promo_code";
import { campaign, getCampaignStopBySlug } from "../../config/campaign";
import { isEventSalesClosed, sortEventPricings } from "../../utils";

type SelectedTicket = { pricing: Ticket; ticket_number: number };

const countryCodes = [
  { code: "250", flag: "🇷🇼", name: "Rwanda", localExample: "7XXXXXXXX" },
  { code: "254", flag: "🇰🇪", name: "Kenya", localExample: "7XXXXXXXX" },
  { code: "256", flag: "🇺🇬", name: "Uganda", localExample: "7XXXXXXXX" },
  { code: "255", flag: "🇹🇿", name: "Tanzania", localExample: "7XXXXXXXX" },
  { code: "257", flag: "🇧🇮", name: "Burundi", localExample: "7XXXXXXX" },
  { code: "243", flag: "🇨🇩", name: "DR Congo", localExample: "8XXXXXXXX" },
];

const defaultCountryCode = "250";

export default function EventDetails() {
  const [cerror, setCerror] = useState(false);
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { data: campaignEvents, isLoading: isCampaignEventsLoading } =
    useGetEventsQuery({
      pageSize: campaign.stops.length,
      search: campaign.title,
    });
  const eventFromList = campaignEvents?.list.find((item) => item.slug === slug);
  const { data: singleEvent, isLoading: isSingleEventLoading } =
    useGetSingleEventQuery(
      { slug: slug ?? "" },
      { skip: !slug || Boolean(eventFromList) }
    );
  const data = eventFromList ?? singleEvent;
  const isLoading = !data && (isCampaignEventsLoading || isSingleEventLoading);
  const [steps, setSteps] = useState<1 | 2 | 3>(1);
  const [selected, setSelected] = useState<SelectedTicket[]>([]);
  const [paymentMode] = useState<"momo" | "code">("momo");
  const [payment, payOptions] = usePaymentMutation();
  const [verify, promoOptions] = useVerifyPromoCodeMutation();
  const { user } = useAppSelector((state) => state.userState);
  const selectedStop =
    getCampaignStopBySlug(slug) ??
    campaign.stops.find((stop) => stop.id === searchParams.get("stop")) ??
    campaign.stops[0];
  const isSalesClosed =
    isEventSalesClosed(data) ||
    isEventSalesClosed({ endDate: selectedStop.isoDate, status: "active" });

  const { handleSubmit, register, formState: { errors }, reset, watch, setError } = useForm({
    defaultValues: {
      email: user?.email ?? "",
      name: user?.name ?? "",
      phoneNumber: "",
      phoneCountryCode: defaultCountryCode,
      payment_mode: paymentMode,
      promoCode: null,
      personalPhoneNumber: getLocalPhoneNumber(user?.phoneNumber),
      personalPhoneCountryCode: defaultCountryCode,
    },
  });

  const getTicketNumber = (id: string): number => {
    const selectedTicket = selected.find((ticket) => ticket.pricing.id === id);
    return selectedTicket ? selectedTicket.ticket_number : 0;
  };

  const addToSelected = (sP: Ticket) => {
    if (isSalesClosed) return;

    setSelected((prevSelected) => {
      const existingTicket = prevSelected.find(
        (ticket) => ticket.pricing.id === sP.id
      );

      if (existingTicket) {
        return prevSelected.map((ticket) =>
          ticket.pricing.id === sP.id
            ? { ...ticket, ticket_number: ticket.ticket_number + 1 }
            : ticket
        );
      }

      return [...prevSelected, { pricing: sP, ticket_number: 1 }];
    });
  };

  const removeFromSelected = (pricingIdToRemove: string) => {
    setSelected((prevSelected) =>
      prevSelected.flatMap((ticket) => {
        if (ticket.pricing.id !== pricingIdToRemove) return [ticket];
        if (ticket.ticket_number <= 1) return [];
        return [{ ...ticket, ticket_number: ticket.ticket_number - 1 }];
      })
    );
  };

  const getTotal = () =>
    selected.reduce(
      (total, item) => total + item.ticket_number * item.pricing.price,
      0
    );

  useEffect(() => {
    setCerror(false);
  }, [steps]);

  useEffect(() => {
    setSteps(1);
    setSelected([]);
  }, [slug]);

  useEffect(() => {
    if (isSalesClosed) {
      setSteps(1);
      setSelected([]);
    }
  }, [isSalesClosed]);

  const handlePayment = (data: any) => {
    if (isSalesClosed) {
      setCerror(true);
      setSteps(1);
      setSelected([]);
      return;
    }

    const {
      personalPhoneCountryCode,
      phoneCountryCode,
      personalPhoneNumber,
      phoneNumber,
      ...rest
    } = data;
    const refined = {
      ...rest,
      personalPhoneNumber: formatInternationalPhone(
        personalPhoneCountryCode,
        personalPhoneNumber
      ),
      phoneNumber: formatInternationalPhone(phoneCountryCode, phoneNumber),
      pricingIds: selected.map((it) => {
        return {
          pricingId: it.pricing.id,
          ticket_number: it.ticket_number,
        };
      }),
    } as PaymentPayload;
    payment({ status: !!user, data: refined });
  };

  useEffect(() => {
    if (payOptions.isSuccess) {
      setSteps(1);
      setSelected([]);
      reset();
    }
  }, [payOptions.isSuccess, reset]);

  const visiblePricings =
    data?.pricings ? sortEventPricings(data.pricings) : [];
  const posterImage = data?.image || campaign.poster;

  const getGatePrice = (item: Ticket) => {
    const gate = campaign.fallbackTickets.find(
      (fallback) => fallback.type === item.type.toUpperCase()
    );
    return gate && gate.price > item.price ? gate.price : undefined;
  };

  return (
    <AnimatedContainer>
      <div className="min-h-screen bg-[#06130a] text-white">
        {isLoading ? (
          <div className="flex min-h-[500px] w-full items-center justify-center text-center text-white/60">
            One moment please...
          </div>
        ) : null}

        {data && (
          <AnimatedContainer>
            <section className="relative overflow-hidden">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url('${posterImage}')` }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#06130a] via-[#150202]/90 to-[#143d20]/50" />
              <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#06130a] to-transparent" />

              <div className="relative mx-auto grid w-full max-w-[1500px] grid-cols-1 items-end gap-8 px-5 py-12 md:px-12 lg:grid-cols-[1fr_360px] lg:px-16">
                <div className="max-w-3xl">
                  <p className="text-sm font-black uppercase tracking-[0.28em] text-[#e8b64c]">
                    {data.organiser || campaign.organiser} presents
                  </p>
                  <h1 className="mt-4 text-5xl font-black uppercase leading-[0.92] text-[#f6d98a] sm:text-7xl">
                    {campaign.title}
                  </h1>
                  <p className="mt-4 text-2xl font-black text-white sm:text-4xl">
                    {campaign.season} — {campaign.year}
                  </p>
                  <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <EventFact icon={<FiMapPin />} label="Venue" value={selectedStop.city} />
                    <EventFact icon={<FiCalendar />} label="Date" value={`Sun. ${selectedStop.shortDate}`} />
                    <EventFact
                      icon={<FiMic />}
                      label="Presented by"
                      value={campaign.organiser}
                    />
                  </div>
                </div>

                <div className="hidden overflow-hidden rounded-lg border border-white/15 bg-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.35)] lg:block">
                  <div
                    className="aspect-[4/5] bg-cover bg-center"
                    style={{ backgroundImage: `url('${posterImage}')` }}
                  />
                </div>
              </div>
            </section>

            <main className="bg-[#f7f4e9] text-[#06130a]">
              <div className="mx-auto grid w-full max-w-[1500px] grid-cols-1 gap-8 px-5 py-10 md:px-12 lg:grid-cols-[1fr_380px] lg:px-16">
                <section>
                  <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div>
                      <p className="text-sm font-black uppercase tracking-[0.2em] text-[#9d1c1c]">
                        Tickets
                      </p>
                      <h2 className="mt-2 text-3xl font-black uppercase text-[#06130a] sm:text-4xl">
                        Choose your tier.
                      </h2>
                    </div>

                    <div className="flex items-center gap-2 text-sm font-bold text-[#143d20]">
                      <FiMusic />
                      Sunday {selectedStop.date} — {campaign.venue}
                    </div>
                  </div>

                  {cerror && (
                    <Alert tone="error">
                      {isSalesClosed
                        ? "Ticket sales are closed for this event."
                        : "Please select tickets."}
                    </Alert>
                  )}
                  {isSalesClosed && !cerror && (
                    <Alert tone="error">
                      Ticket sales for this event are closed.
                    </Alert>
                  )}
                  {payOptions.isSuccess && (
                    <Alert tone="success">Payment initiated.</Alert>
                  )}
                  {payOptions.isError && (
                    <Alert tone="error">
                      {payOptions.error
                        ? (payOptions.error as any).data?.message
                        : "Something went wrong, payment failed"}
                    </Alert>
                  )}

                  {steps === 1 && (
                    <>
                      <div className="mt-6 grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                        {visiblePricings.length === 0 ? (
                          <div className="col-span-2 flex min-h-32 items-center justify-center text-center text-[#143d20]/70">
                            No tickets yet
                          </div>
                        ) : null}
                        {visiblePricings.map((item) => (
                          <div
                            key={item.id}
                            className={`flex flex-wrap items-center justify-between gap-5 rounded-lg border border-[#ddd6bd] bg-white p-4 shadow-[0_18px_50px_rgba(18,2,2,0.08)] ${
                              isSalesClosed ? "opacity-70" : ""
                            }`}
                          >
                            <div className="flex flex-row items-center gap-4">
                              <div className="rounded-md bg-[#efe8cd] p-3 text-2xl text-[#9d1c1c]">
                                <BsTicketPerforated />
                              </div>
                              <div className="flex flex-col gap-1 font-semibold capitalize">
                                <p className="text-lg font-black uppercase">
                                  {item.type}
                                </p>
                                <p>
                                  <b>{item.price.toLocaleString()}</b>{" "}
                                  {data.currency}
                                </p>
                                {getGatePrice(item) ? (
                                  <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#9d1c1c]">
                                    <span className="text-[#143d20]/45 line-through">
                                      {getGatePrice(item)!.toLocaleString()}{" "}
                                      {data.currency}
                                    </span>{" "}
                                    Pre-sell price
                                  </p>
                                ) : null}
                              </div>
                            </div>
                            <div className="flex flex-row items-center text-[#143d20]">
                              <button
                                type="button"
                                onClick={() => removeFromSelected(item.id)}
                                disabled={isSalesClosed}
                                className="rounded-md p-2 text-[1.5rem] transition hover:bg-[#efe8cd] disabled:cursor-not-allowed disabled:opacity-40"
                                aria-label={`Remove ${item.type} ticket`}
                              >
                                <CgChevronLeftO />
                              </button>
                              <div className="mx-2 min-w-11 rounded-md bg-[#efe8cd] px-3 py-2 text-center text-[1rem] font-black text-[#9d1c1c]">
                                {getTicketNumber(item.id)}
                              </div>
                              <button
                                type="button"
                                onClick={() => addToSelected(item)}
                                disabled={isSalesClosed}
                                className="rounded-md p-2 text-[1.5rem] transition hover:bg-[#efe8cd] disabled:cursor-not-allowed disabled:opacity-40"
                                aria-label={`Add ${item.type} ticket`}
                              >
                                <CgChevronRightO />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          isSalesClosed || selected.length === 0
                            ? setCerror(true)
                            : setSteps(2)
                        }
                        disabled={isSalesClosed}
                        className="mt-6 flex w-full items-center justify-center gap-3 rounded-md bg-[#9d1c1c] py-4 font-black uppercase tracking-[0.12em] text-white transition hover:bg-[#b52a2a] disabled:cursor-not-allowed disabled:bg-gray-300 sm:w-60"
                      >
                        {isSalesClosed ? "Sales closed" : "Buy tickets"}{" "}
                        <GoArrowRight className="text-2xl" />
                      </button>
                    </>
                  )}

                  {steps === 2 && (
                    <>
                      <div className="mt-6 flex flex-col gap-3">
                        <div className="flex flex-row items-center gap-2 py-3 font-black uppercase tracking-[0.12em]">
                          <IoArrowBackCircleOutline
                            onClick={() => setSteps(1)}
                            className="cursor-pointer text-2xl"
                          />{" "}
                          Summary
                        </div>
                        <div className="rounded-lg border border-[#ddd6bd] bg-white p-4">
                          {selected.map((sel) => (
                            <div
                              key={sel.pricing.id}
                              className="mb-3 flex flex-row justify-between border-b border-[#ddd6bd] pb-3 last:mb-0 last:border-b-0 last:pb-0"
                            >
                              <div className="flex flex-col font-black uppercase">
                                {sel.pricing.type}
                                <p className="text-sm font-bold text-[#9d1c1c]">
                                  {sel.ticket_number} Ticket
                                  {sel.ticket_number > 1 ? "s" : ""}
                                </p>
                              </div>
                              <p className="font-black">
                                {(
                                  sel.pricing.price * sel.ticket_number
                                ).toLocaleString()}{" "}
                                {data.currency}
                              </p>
                            </div>
                          ))}
                        </div>
                        <div className="flex flex-row justify-between rounded-lg bg-[#06130a] p-4 font-black uppercase text-white">
                          <h3>Total amount</h3>
                          <p>
                            {getTotal().toLocaleString()} {data.currency}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSteps(3)}
                        className="mt-6 flex w-full items-center justify-center gap-3 rounded-md bg-[#9d1c1c] py-4 font-black uppercase tracking-[0.12em] text-white transition hover:bg-[#b52a2a] sm:w-60"
                      >
                        Checkout <GoArrowRight className="text-2xl" />
                      </button>
                    </>
                  )}

                  {steps === 3 && (
                    <form
                      className="mt-6 flex w-full flex-col items-start"
                      onSubmit={handleSubmit(handlePayment)}
                    >
                      <div className="w-full">
                        <div className="flex flex-row items-center gap-2 py-3 font-black uppercase tracking-[0.12em]">
                          <IoArrowBackCircleOutline
                            onClick={() => setSteps(2)}
                            className="cursor-pointer text-2xl"
                          />{" "}
                          Checkout
                        </div>
                        <div className="grid w-full grid-cols-1 gap-4 rounded-lg border border-[#ddd6bd] bg-white p-4 sm:grid-cols-2">
                          <div className="col-span-2 flex flex-col gap-2">
                            <label htmlFor="email" className="font-bold">
                              Email
                            </label>
                            <input
                              type="email"
                              {...register("email", {
                                required: "Email is required",
                              })}
                              className="w-full rounded-md border border-[#ddd6bd] bg-[#f7f4e9] px-3 py-3 focus:outline-none"
                              placeholder="Enter your email address"
                            />
                            {errors.email && (
                              <p className="text-sm text-red-500">
                                {errors.email.message}
                              </p>
                            )}
                          </div>
                          <div className="col-span-2 flex flex-col gap-2 sm:col-span-1">
                            <label htmlFor="name" className="font-bold">
                              Names
                            </label>
                            <input
                              type="text"
                              {...register("name", {
                                required: "Name is required",
                              })}
                              className="w-full rounded-md border border-[#ddd6bd] bg-[#f7f4e9] px-3 py-3 focus:outline-none"
                              placeholder="Enter your full names"
                            />
                            {errors.name && (
                              <p className="text-sm text-red-500">
                                {errors.name.message}
                              </p>
                            )}
                          </div>
                          <div className="col-span-2 flex flex-col gap-2 sm:col-span-1">
                            <label htmlFor="personalPhoneNumber" className="font-bold">
                              Phone number
                            </label>
                            <PhoneInput
                              codeRegistration={register("personalPhoneCountryCode")}
                              phoneRegistration={register("personalPhoneNumber", {
                                required: "Phone number is required",
                                validate: (value) =>
                                  isValidPhoneNumber(
                                    value,
                                    watch("personalPhoneCountryCode")
                                  ) || "Phone number invalid",
                              })}
                              placeholder={
                                countryCodes.find(
                                  (country) =>
                                    country.code ===
                                    watch("personalPhoneCountryCode")
                                )?.localExample ?? "7XXXXXXXX"
                              }
                            />
                            {errors.personalPhoneNumber && (
                              <p className="text-sm text-red-500">
                                {errors.personalPhoneNumber.message}
                              </p>
                            )}
                          </div>
                          <div className="col-span-2 hidden border-y py-3">
                            <div className="flex flex-col gap-2">
                              <label htmlFor="promoCode" className="font-bold">
                                Promo Code{" "}
                                <span className="text-xs text-primary">
                                  (optional)
                                </span>
                              </label>
                              <div className="flex flex-row gap-1">
                                <input
                                  type="text"
                                  {...register("promoCode")}
                                  className="w-full rounded-md border border-[#ddd6bd] bg-[#f7f4e9] px-3 py-3 focus:outline-none"
                                  placeholder="Enter a promo code"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    watch("promoCode")
                                      ? verify({
                                          promo: watch("promoCode") ?? "",
                                        })
                                      : setError("promoCode", {
                                          message: "Promo code is required",
                                        })
                                  }
                                  className="rounded-md bg-primary p-2 text-white"
                                >
                                  {promoOptions.isLoading
                                    ? "Loading"
                                    : "Verify"}
                                </button>
                              </div>
                              {errors.promoCode && (
                                <p className="text-sm text-red-500">
                                  {errors.promoCode.message}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="col-span-2 flex items-center justify-center">
                            <img src={mtn_logo} className="w-20" alt="mtn-logo" />
                          </div>
                          <div className="col-span-2 flex flex-col gap-2">
                            <label htmlFor="phoneNumber" className="font-bold">
                              Momo Phone number
                            </label>
                            <PhoneInput
                              codeRegistration={register("phoneCountryCode")}
                              phoneRegistration={register("phoneNumber", {
                                required: "Momo phone number is required",
                                validate: (value) =>
                                  isValidPhoneNumber(
                                    value,
                                    watch("phoneCountryCode")
                                  ) || "Phone number invalid",
                              })}
                              placeholder={
                                countryCodes.find(
                                  (country) =>
                                    country.code === watch("phoneCountryCode")
                                )?.localExample ?? "7XXXXXXXX"
                              }
                            />
                            {errors.phoneNumber && (
                              <p className="text-sm text-red-500">
                                {errors.phoneNumber.message}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        disabled={payOptions.isLoading}
                        className="mt-6 flex w-full items-center justify-center gap-3 rounded-md bg-[#9d1c1c] py-4 font-black uppercase tracking-[0.12em] text-white transition hover:bg-[#b52a2a] disabled:bg-gray-300 sm:w-60"
                      >
                        {payOptions.isLoading ? "Loading..." : "Pay Now"}
                      </button>
                    </form>
                  )}
                </section>

                <aside className="h-fit rounded-lg bg-[#06130a] p-5 text-white shadow-[0_24px_70px_rgba(18,2,2,0.18)] lg:sticky lg:top-28">
                  <div
                    className="aspect-[4/5] rounded-lg bg-cover bg-center"
                    style={{ backgroundImage: `url('${posterImage}')` }}
                  />
                  <div className="mt-5">
                    <p className="text-sm font-black uppercase tracking-[0.2em] text-[#e8b64c]">
                      Venue
                    </p>
                    <h3 className="mt-2 text-3xl font-black text-[#f6d98a]">
                      {selectedStop.city}
                    </h3>
                    <p className="mt-2 flex items-center gap-2 text-white/65">
                      <FiCalendar /> {selectedStop.date}
                    </p>
                    <p className="mt-2 flex items-center gap-2 text-white/65">
                      <FiMapPin /> Rwanda
                    </p>
                    {isSalesClosed ? (
                      <p className="mt-4 rounded-md bg-white/10 px-3 py-2 text-sm font-black uppercase tracking-[0.12em] text-[#e8b64c]">
                        Sales closed
                      </p>
                    ) : null}
                  </div>
                  <div className="mt-5 border-t border-white/10 pt-5">
                    <p className="text-sm font-black uppercase tracking-[0.2em] text-[#e8b64c]">
                      Entrance fees
                    </p>
                    <div className="mt-3 flex flex-col gap-2">
                      {visiblePricings.map((item) => (
                        <div
                          key={item.id}
                          className="flex flex-col gap-0.5 border-b border-white/10 pb-2 text-sm last:border-b-0"
                        >
                          <div className="flex justify-between">
                            <span className="font-black uppercase">{item.type}</span>
                            <span>{item.price.toLocaleString()} {data.currency}</span>
                          </div>
                          {getGatePrice(item) ? (
                            <span className="self-end text-xs font-bold uppercase tracking-[0.08em] text-[#e8b64c]">
                              Pre-sell — gate {getGatePrice(item)!.toLocaleString()} {data.currency}
                            </span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                </aside>
              </div>
            </main>
          </AnimatedContainer>
        )}
      </div>
    </AnimatedContainer>
  );
}

function EventFact({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.08] p-4 backdrop-blur">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#9d1c1c] text-xl text-white">
          {icon}
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-white/45">
            {label}
          </p>
          <p className="text-lg font-black text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

function Alert({
  children,
  tone,
}: {
  children: ReactNode;
  tone: "error" | "success";
}) {
  const isError = tone === "error";

  return (
    <motion.div
      initial={{ y: "-10px", opacity: 0.5 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`mt-4 flex w-full flex-row items-center gap-2 border-l-4 px-3 py-2 text-sm ${
        isError
          ? "border-red-500 bg-red-50 text-red-700"
          : "border-green-500 bg-green-50 text-green-700"
      }`}
    >
      <HiOutlineExclamationCircle />
      {children}
    </motion.div>
  );
}

function PhoneInput({
  codeRegistration,
  phoneRegistration,
  placeholder,
}: {
  codeRegistration: any;
  phoneRegistration: any;
  placeholder: string;
}) {
  return (
    <div className="flex w-full overflow-hidden rounded-md border border-[#ddd6bd] bg-[#f7f4e9] focus-within:border-[#9d1c1c]">
      <select
        {...codeRegistration}
        className="min-w-[132px] border-r border-[#ddd6bd] bg-[#efe8cd] px-3 py-3 text-sm font-black text-[#06130a] focus:outline-none"
        aria-label="Country code"
      >
        {countryCodes.map((country) => (
          <option key={country.code} value={country.code}>
            {country.flag} +{country.code}
          </option>
        ))}
      </select>
      <input
        type="tel"
        inputMode="numeric"
        {...phoneRegistration}
        className="min-w-0 flex-1 bg-transparent px-3 py-3 focus:outline-none"
        placeholder={placeholder}
      />
    </div>
  );
}

function getLocalPhoneNumber(phoneNumber?: string) {
  if (!phoneNumber) return "";
  const normalized = phoneNumber.replace(/\D/g, "");
  const country = countryCodes.find((item) => normalized.startsWith(item.code));

  return country ? normalized.slice(country.code.length) : normalized;
}

function formatInternationalPhone(countryCode: string, localPhoneNumber: string) {
  return `${countryCode}${localPhoneNumber.replace(/\D/g, "").replace(/^0+/, "")}`;
}

function isValidPhoneNumber(localPhoneNumber: string, countryCode?: string) {
  const normalized = localPhoneNumber.replace(/\D/g, "").replace(/^0+/, "");

  if (countryCode === "250") {
    return /^7[2389]\d{7}$/.test(normalized);
  }

  return /^\d{7,12}$/.test(normalized);
}
